/* eslint-disable camelcase */
import { Box, Button, Card, Chip, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { capturePHEvents } from '../analytics/posthog.analytics';
import { PH_OFFER_CLICK, PH_OFFER_COUPON_COPIED } from '../utils/posthog.eventNames';
import Iconify from '../fl-components/Iconify';
import palette from '../theme/palette';

export default function SimpleOfferCard({ offer, lockOverlay = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const { Coupon_Heading, IMG_URL_1 } = offer;

  function openCouponSite() {
    capturePHEvents(PH_OFFER_CLICK, {
      offerName: Coupon_Heading,
      offerLink: offer?.Coupon_Affiliate_URL ?? offer?.Coupon_URL
    });
    window.open(offer?.Coupon_Affiliate_URL ?? offer?.Coupon_URL, '_blank');
  }

  function onCopy() {
    capturePHEvents(PH_OFFER_COUPON_COPIED, {
      offerName: Coupon_Heading,
      offerLink: offer?.Coupon_Affiliate_URL ?? offer?.Coupon_URL,
      code: offer?.Offer_Code
    });
    enqueueSnackbar('Copied!');
  }

  return (
    <Card sx={{ textAlign: 'center', width: '100%', px: { xs: 2, sm: 3 }, py: 2 }}>
      {lockOverlay && (
        <Box
          sx={{
            height: '100%',
            zIndex: 12,
            backdropFilter: 'blur(20px)',
            width: '100%',
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: 0,
            left: 0
          }}
        >
          <Tooltip title="Please complete your profile to unlock">
            <Box sx={{ border: '1px solid', borderRadius: '15px', padding: '15px' }}>
              <Iconify icon="bx:lock" width={30} height={30} />
            </Box>
          </Tooltip>
        </Box>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'center', md: 'unset' }}
        alignItems={{ xs: 'center', md: 'center' }}
        spacing={1}
        sx={{ height: '100%' }}
      >
        <img style={{ width: 100, objectFit: 'contain' }} src={IMG_URL_1} alt={Coupon_Heading} />
        <Box sx={{ flex: 1.8, width: '100%' }}>
          <Tooltip title={Coupon_Heading} placement="bottom" arrow>
            <Typography textAlign={{ xs: 'center', md: 'left' }} variant="subtitle1" px={1}>
              {Coupon_Heading.length > 40
                ? `${Coupon_Heading.substring(0, 40)}...`
                : Coupon_Heading}
            </Typography>
          </Tooltip>
          {offer?.Coupon_Category && (
            <Stack
              direction="row"
              spacing={1}
              mt={1}
              alignItems={{ xs: 'center' }}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
            >
              {offer?.Coupon_Category.toLowerCase()
                .split(',')
                .map((category, index) => {
                  if (index <= 1)
                    return (
                      <Tooltip title={category} arrow>
                        <Chip
                          label={category.length > 7 ? `${category.substring(0, 7)}...` : category}
                          sx={{ textTransform: 'capitalize', borderRadius: 0.8 }}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Tooltip>
                    );
                  return null;
                })}
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2, md: 2 }}
            alignItems={{ sm: 'center' }}
            justifyContent={{
              xs: 'center',
              md: 'flex-start'
            }}
            pt={2}
          >
            {offer?.Offer_Code && (
              <CopyToClipboard text={offer?.Offer_Code} onCopy={() => onCopy()}>
                <Button
                  variant="outlined"
                  sx={{
                    borderStyle: 'dashed',
                    fontSize: 'unset'
                  }}
                >
                  <span style={{ fontSize: 12 }}>
                    {offer?.Offer_Code.length > 7
                      ? `${offer?.Offer_Code.substring(0, 7)}...`
                      : offer?.Offer_Code}
                  </span>
                  <Iconify
                    icon="ic:baseline-content-copy"
                    sx={{ color: palette.primary.main, marginLeft: 1 }}
                    size={20}
                  />
                </Button>
              </CopyToClipboard>
            )}
            <Button variant="contained" onClick={() => openCouponSite()}>
              Details
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}

SimpleOfferCard.propTypes = {
  offer: PropTypes.object,
  lockOverlay: PropTypes.bool
};
